/**
 * useFilterBoxState — state normalizado + facets para consumidores NUEVOS de FilterBox
 * (Data Health, Estadísticas standalone, futuros listados). GH#24 Fase 1.
 *
 * Publicaciones NO usa este hook: reusa el state que ya vive en Dashboard (~90 props)
 * para no regresionar el path caliente. Este hook existe para las secciones que hoy
 * NO tienen caja de filtros y necesitan una desde cero.
 *
 * Modelo IMMEDIATE-APPLY: en Data Health no hay botón "Filtrar" — tildar una opción
 * filtra al toque. Por eso mapeamos toda la selección a `filter` y dejamos `applied`
 * vacío (la mecánica pending-removal de FilterDropdown solo aplica sobre `applied`, así
 * que queda inerte: destildar un `filter` lo saca directo). El consumidor lee
 * `activeFilters` (por dimensión) para acotar su propio fetch de listado.
 *
 * treeMode: el acotado de options hijo→n>0 lo hace FilterBox con la `meta` de facets;
 * acá solo alimentamos options/meta cross-filtradas (useFacetedFilters ya cross-filtra).
 */
import { useState, useCallback, useMemo } from 'react';
import { useFacetedFilters } from './useFacetedFilters';
import {
  FilterBoxConfig,
  DimensionKey,
  DIMENSION_KEYS,
} from '../config/filterBoxConfigs';
import type { DimensionState } from '../components/filters/FilterBox';
import type { FilterDropdownMeta } from '../components/filters/FilterDropdown';

export type ActiveFilters = Partial<Record<DimensionKey, string[]>>;

/**
 * Provider de options/counts para `source.kind:'inline'` (Tendencias, GH#24 Fase 3).
 *
 * En vez de fetchear facets per-toma, el host arma un mapa por dimensión con las
 * options ya cargadas (endpoints distinct-* existentes, universo multi-toma) + counts
 * opcionales (para el cascade de treeMode). Este hook lo consume EXACTAMENTE igual que
 * a `useFacetedFilters`, produciendo la MISMA forma de `dimensions` que consume FilterBox.
 * Así Tendencias entra al mismo pipeline de state que Publicaciones/Estadísticas sin
 * duplicar el mapa DimensionState a mano.
 */
export interface InlineDimensionData {
  /** Opciones visibles del dropdown (ya ordenadas por el host). */
  options: string[];
  /** counts + total por dimensión (opcional; si falta, meta=null → sin cascade). */
  meta?: FilterDropdownMeta | null;
  /** clientAllows AND sectionConfig.enabled. Si false → FilterBox no la renderiza. */
  enabled?: boolean;
}
export type InlineSourceData = Partial<Record<DimensionKey, InlineDimensionData>>;

interface UseFilterBoxStateOptions {
  config: FilterBoxConfig;
  tableName: string;
  /** Master switch (ej. sección visible). Si false, no fetchea facets. */
  enabled?: boolean;
  /**
   * Dimensiones que el cliente permite (caps). Si se omite, se asume que todas las
   * dimensionales de la config están permitidas y se deja que el backend omita las
   * ausentes (facet vacío ⇒ options vacías).
   */
  allowedDimensions?: DimensionKey[];
  /** Debounce del fetch de facets (default 300ms). */
  debounceMs?: number;
  /**
   * Solo para `source.kind:'inline'`: options/counts/enabled por dimensión provistos por
   * el host (derivados de su propia data, ej. distinct-* de Tendencias). Ignorado cuando
   * la fuente es `facets`. La selección viva la sigue manejando este hook (unificado).
   */
  inlineData?: InlineSourceData;
}

// Dimensiones dimensionales habilitadas en la config (aparecen en algún row enabled).
function configDimensions(config: FilterBoxConfig): DimensionKey[] {
  const set = new Set<DimensionKey>();
  for (const row of config.rows) {
    for (const field of row.fields) {
      if (field.enabled && (DIMENSION_KEYS as string[]).includes(field.key)) {
        set.add(field.key as DimensionKey);
      }
    }
  }
  return DIMENSION_KEYS.filter(k => set.has(k)); // orden canónico
}

const EMPTY: string[] = [];

export function useFilterBoxState(options: UseFilterBoxStateOptions) {
  const { config, tableName, enabled = true, allowedDimensions, debounceMs, inlineData } = options;

  const isInline = config.source.kind === 'inline';

  const dims = useMemo(() => configDimensions(config), [config]);
  const allowed = useMemo(
    () => (allowedDimensions ? dims.filter(d => allowedDimensions.includes(d)) : dims),
    [dims, allowedDimensions],
  );

  // Selección viva por dimensión (immediate-apply).
  const [selected, setSelected] = useState<ActiveFilters>({});

  const setDimFilter = useCallback((dim: DimensionKey, value: string[]) => {
    setSelected(prev => ({ ...prev, [dim]: value }));
  }, []);

  // Filtros pipe-separated para el endpoint de facets (cross-filter de counts).
  const facetFilters = useMemo(() => {
    const f: Record<string, string> = {};
    for (const dim of allowed) {
      const vals = selected[dim];
      if (vals && vals.length > 0) f[dim] = vals.join('|');
    }
    return f;
  }, [selected, allowed]);

  const includeHidden =
    config.source.kind === 'facets' ? !!config.source.includeHidden : false;

  const faceted = useFacetedFilters(facetFilters, {
    tableName,
    enabled: enabled && config.source.kind === 'facets',
    dimensions: allowed,
    includeHidden,
    debounceMs,
  });

  // Mapa DimensionState que consume FilterBox.
  // - facets: options/meta salen de useFacetedFilters (path caliente intacto).
  // - inline: options/meta/enabled los aporta el host vía `inlineData`; la selección
  //   viva y los setters los sigue manejando este hook (mismo pipeline unificado).
  const dimensions = useMemo(() => {
    const map: Partial<Record<DimensionKey, DimensionState>> = {};
    for (const dim of dims) {
      if (isInline) {
        const data = inlineData?.[dim];
        // enabled efectivo = caps del cliente (allowed) AND lo que el host declare.
        const hostEnabled = data?.enabled ?? true;
        map[dim] = {
          filter: selected[dim] || EMPTY,
          applied: EMPTY,
          pendingRemove: EMPTY,
          setFilter: (value: string[]) => setDimFilter(dim, value),
          options: data?.options || EMPTY,
          meta: data?.meta ?? null,
          enabled: allowed.includes(dim) && hostEnabled,
        };
      } else {
        map[dim] = {
          filter: selected[dim] || EMPTY,
          applied: EMPTY,
          pendingRemove: EMPTY,
          setFilter: (value: string[]) => setDimFilter(dim, value),
          options: faceted.getOptions(dim),
          meta: faceted.getMeta(dim),
          enabled: allowed.includes(dim),
        };
      }
    }
    return map;
  }, [dims, allowed, selected, faceted, setDimFilter, isInline, inlineData]);

  const activeFilters: ActiveFilters = useMemo(() => {
    const f: ActiveFilters = {};
    for (const dim of allowed) {
      const vals = selected[dim];
      if (vals && vals.length > 0) f[dim] = vals;
    }
    return f;
  }, [selected, allowed]);

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const clearAll = useCallback(() => setSelected({}), []);

  return {
    dimensions,
    activeFilters,
    hasActiveFilters,
    clearAll,
    loading: faceted.loading,
    error: faceted.error,
    /** Setter directo por dimensión (host inline: p.ej. limpiar una dim al cambiar groupBy). */
    setDimFilter,
  };
}
